import type { ResumenCertificado } from '@shared/certificados';

/** Respuesta de GET /api/certificados/estado. */
export interface ResumenCertificadoApi extends Omit<ResumenCertificado, 'nombre' | 'validUntil'> {
  /** El servidor manda `null` si falta el campo en el documento. */
  id: string | null;
  nombre: string | null;
  validUntil: string | null;
}

export interface EstadoCertificadosApi {
  activo: ResumenCertificadoApi | null;
  preparado: ResumenCertificadoApi | null;
  pendientesMigrar: number;
}

export type ColorAviso = 'amarillo' | 'naranja' | 'rojo';

export interface Aviso {
  color: ColorAviso;
  /** `alert` solo en los niveles urgentes; el resto, `status`. */
  rol: 'alert' | 'status';
  texto: string;
  /** Solo el nivel «aviso» se puede ocultar (7 días). */
  ocultable: boolean;
  accion: { texto: string; a: string };
}

/** Fecha en el formato de la aplicación (dd/mm/aaaa, hora de Madrid). */
export function formatearFecha(iso: string | null | undefined): string {
  const fecha = new Date(iso ?? '');
  // Sin fecha válida no se inventa texto: quien llama decide qué decir.
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Madrid',
  });
}

const PREPARADO = 'Tienes un certificado nuevo preparado: compruébalo y actívalo.';
const SIN_FECHA = 'no tiene una fecha de caducidad válida';
const ENVIOS_FALLANDO = 'Los envíos a la AEAT y a FACe están fallando.';

/** Hora (hh:mm) en Madrid. */
function formatearHora(iso: string | null | undefined): string {
  return new Date(iso ?? '').toLocaleTimeString('es-ES', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Europe/Madrid',
  });
}

/** «caduca el dd/mm/aaaa (en N días)»; con 0 días, con la hora de Madrid para no contradecir la fecha. */
export function textoCaducidad(iso: string | null | undefined, dias: number): string {
  if (!formatearFecha(iso)) return SIN_FECHA;
  if (dias === 0) {
    return `caduca en menos de 24 horas (el ${formatearFecha(iso)} a las ${formatearHora(iso)})`;
  }
  return `caduca el ${formatearFecha(iso)} (en ${dias} ${dias === 1 ? 'día' : 'días'})`;
}

/** « «Nombre»» con el espacio delante, o nada si el servidor no mandó nombre. */
function entreComillas(nombre: string | null | undefined): string {
  return nombre ? ` «${nombre}»` : '';
}

/**
 * Qué aviso global muestra la aplicación según el estado de los certificados,
 * o `null` si no hay nada que avisar (más de 60 días de margen o aún sin cargar).
 */
export function describirAviso(estado: EstadoCertificadosApi | null): Aviso | null {
  if (!estado) return null;

  const { activo, preparado } = estado;
  if (!activo) {
    return {
      color: 'rojo',
      rol: 'alert',
      texto: 'No tienes ningún certificado digital activo: los envíos a la AEAT y a FACe no funcionarán.',
      ocultable: false,
      accion: { texto: 'Subir certificado', a: '/certificados' },
    };
  }
  if (activo.nivel === 'ok') return null;

  // Un preparado caducado (o con fecha inválida) no sirve: no se empuja a activarlo.
  const preparadoUtil = preparado && preparado.nivel !== 'caducado' ? preparado : null;
  const notaPreparado = preparado && !preparadoUtil ?
    ` El certificado preparado${entreComillas(preparado.nombre)} está caducado: no sirve para activarlo.` :
    '';
  const nombre = entreComillas(activo.nombre).trim();
  const accion = preparadoUtil ?
    { texto: 'Activar', a: '/certificados' } :
    { texto: 'Renovar', a: '/certificados' };
  const propio = `Tu certificado ${nombre ? `${nombre} ` : ''}${textoCaducidad(activo.validUntil, activo.diasRestantes)}.`;

  switch (activo.nivel) {
    case 'aviso':
    case 'importante':
      return {
        color: activo.nivel === 'aviso' ? 'amarillo' : 'naranja',
        rol: 'status',
        texto: preparadoUtil ? PREPARADO : propio + notaPreparado,
        ocultable: activo.nivel === 'aviso',
        accion,
      };
    case 'urgente':
      return {
        color: 'rojo',
        rol: 'alert',
        texto: preparadoUtil ? PREPARADO : propio + notaPreparado,
        ocultable: false,
        accion,
      };
    case 'caducado':
      return {
        color: 'rojo',
        rol: 'alert',
        texto: preparadoUtil ?
          `${ENVIOS_FALLANDO} ${PREPARADO}` :
          `Tu certificado ${nombre ? `${nombre} ` : ''}${
            formatearFecha(activo.validUntil) ? 'ha caducado' : SIN_FECHA
          }. ${ENVIOS_FALLANDO}${notaPreparado}`,
        ocultable: false,
        accion,
      };
    default: {
      // En compilación, un nivel nuevo sin tratar es un error; en ejecución (un servidor más
      // nuevo que una pestaña con JS viejo) no se tira la interfaz: aviso prudente.
      const inalcanzable: never = activo.nivel;
      void inalcanzable;
      return {
        color: 'rojo',
        rol: 'alert',
        texto: preparadoUtil ? PREPARADO : propio + notaPreparado,
        ocultable: false,
        accion: { texto: 'Renovar', a: '/certificados' },
      };
    }
  }
}

const CLAVE_OCULTO = 'certificado-aviso-oculto-hasta';
const SIETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;

/** Lo mínimo que se usa de `localStorage` (que puede fallar o no existir). */
export interface Almacen {
  getItem(clave: string): string | null;
  setItem(clave: string, valor: string): void;
}

/**
 * ¿Ocultó el usuario el aviso de ESTE certificado hace menos de 7 días?
 * Solo vale `ahora < hasta <= ahora + 7 días` y el mismo id; cualquier otra
 * cosa (corrupto, futuro lejano, otro certificado, almacén roto) cuenta como «no».
 */
export function ocultoHasta(almacen: Almacen | undefined, ahora: Date, certificadoId: string): boolean {
  try {
    const crudo = almacen?.getItem(CLAVE_OCULTO);
    if (!crudo) return false;
    const guardado: unknown = JSON.parse(crudo);
    if (typeof guardado !== 'object' || guardado === null) return false;
    const { id, hasta } = guardado as { id?: unknown; hasta?: unknown };
    if (id !== certificadoId || typeof hasta !== 'number' || !Number.isFinite(hasta)) return false;
    const t = ahora.getTime();
    return hasta > t && hasta <= t + SIETE_DIAS_MS;
  } catch {
    return false;
  }
}

/** Oculta el aviso de este certificado 7 días. Si el almacén falla, simplemente no se recuerda. */
export function ocultarSieteDias(almacen: Almacen | undefined, ahora: Date, certificadoId: string): void {
  try {
    almacen?.setItem(CLAVE_OCULTO, JSON.stringify({ id: certificadoId, hasta: ahora.getTime() + SIETE_DIAS_MS }));
  } catch {
    // Modo privado o almacenamiento bloqueado: el aviso reaparecerá al recargar.
  }
}
